/**
 * BIC live auto-derive from an IBAN field.
 *
 * Wires a debounced GET to ``/office/api/derive-bic/?iban=…`` so the
 * BIC input gets filled as the user types/pastes their IBAN, **without
 * having to submit the form first**. Only fills when the BIC input is
 * empty, so a user-typed BIC is never overwritten.
 *
 * Why server-side: schwifty (the IBAN → BIC bank-code DB) is Python and
 * weighs a few hundred KB. We don't ship that to the browser; we ask
 * the backend instead. The same endpoint powers the Settings → Billing
 * → BankAccount form, so this helper is shared.
 *
 * Usage:
 *
 *     import { attachBicAutoDerive } from
 *         '../utils/forms/bic-autoderive.js';
 *
 *     attachBicAutoDerive({
 *         ibanInput: form.querySelector('[name="iban"]'),
 *         bicInput: form.querySelector('[name="bic"]'),
 *     });
 *
 * Idempotent: a per-element JS-property flag (``_bicAutoderiveBound``)
 * prevents double-binding when the wiring runs on every boosted
 * re-init (see ``.cursor/rules/hx-boost-init.mdc`` rule 4).
 */

var DEBOUNCE_MS = 350;
var MIN_IBAN_CHARS = 15;       // shortest national IBAN is BE = 16, NL = 18; 15 is the safety floor
var ENDPOINT = '/office/api/derive-bic/';

function getCsrfToken() {
    // Used only if we ever need POST; GET works without it. Kept here
    // so we can switch to POST without re-touching call sites.
    var match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
    return match ? match[1] : '';
}

export function attachBicAutoDerive(opts) {
    var ibanInput = opts && opts.ibanInput;
    var bicInput = opts && opts.bicInput;
    if (!ibanInput || !bicInput) return;
    if (ibanInput._bicAutoderiveBound) return;
    ibanInput._bicAutoderiveBound = true;

    var timer = null;
    var inflight = null;       // AbortController of the in-flight request, if any

    function cancelInflight() {
        if (inflight) {
            try { inflight.abort(); } catch (_e) { /* noop */ }
            inflight = null;
        }
    }

    function normaliseClient(value) {
        return (value || '')
            .replace(/\s+/g, '')
            .replace(/\u00a0/g, '')
            .toUpperCase();
    }

    function scheduleLookup() {
        if (timer) clearTimeout(timer);
        timer = setTimeout(runLookup, DEBOUNCE_MS);
    }

    async function runLookup() {
        // Never overwrite a user-typed BIC.
        if ((bicInput.value || '').trim() !== '') return;

        var iban = normaliseClient(ibanInput.value);
        if (iban.length < MIN_IBAN_CHARS) return;

        cancelInflight();
        inflight = (typeof AbortController !== 'undefined') ? new AbortController() : null;

        try {
            var url = ENDPOINT + '?iban=' + encodeURIComponent(iban);
            var response = await fetch(url, {
                method: 'GET',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                credentials: 'same-origin',
                signal: inflight ? inflight.signal : undefined,
            });
            if (!response.ok) return;
            var data = await response.json();
            var derived = (data && data.bic) ? String(data.bic) : '';
            // Re-check: the user may have started typing a BIC during
            // the round-trip — don't clobber that.
            if (derived && (bicInput.value || '').trim() === '') {
                bicInput.value = derived;
                // Native ``input`` event so any downstream listener
                // (validation, dirty-state tracker, etc.) sees the
                // programmatic change.
                try {
                    bicInput.dispatchEvent(new Event('input', { bubbles: true }));
                } catch (_e) {
                    var evt = document.createEvent('Event');
                    evt.initEvent('input', true, true);
                    bicInput.dispatchEvent(evt);
                }
            }
        } catch (err) {
            // AbortError is expected when the user keeps typing.
            if (err && err.name === 'AbortError') return;
            // Silent failure — server-side ``clean()`` is the fallback.
            if (window && window.console) {
                window.console.debug('[bic-autoderive] lookup failed', err);
            }
        } finally {
            inflight = null;
        }
    }

    ibanInput.addEventListener('input', scheduleLookup);
    // Paste fires before the value lands on some browsers; debounce
    // covers it, but listening explicitly avoids a paste-then-tab race.
    ibanInput.addEventListener('change', scheduleLookup);
    ibanInput.addEventListener('blur', function() {
        // Flush immediately on blur if there is still a pending debounce.
        if (timer) {
            clearTimeout(timer);
            timer = null;
            runLookup();
        }
    });

    // Defensive: suppress an unused-var lint on getCsrfToken if eslint
    // grumbles. Exposed-for-extension, not removed.
    void getCsrfToken;
}
